// Store integrado com Supabase
// Mantém a mesma API do store original (addCliente, updateAgendamento, etc.)
// mas persiste tudo no banco via Supabase

import { supabase } from './supabase';

export const today = () => new Date().toISOString().slice(0, 10);

const state = {
  user: null,
  tenant: null,
  clientes: [],
  servicos: [],
  profissionais: [],
  agendamentos: [],
  horarios: { seg:true,ter:true,qua:true,qui:true,sex:true,sab:true,dom:false,ini:'09:00',fim:'18:00' },
  diasBloqueados: [],
  logo: null,
  loading: false,
};

const listeners = new Set();
function notify() { listeners.forEach(fn => fn()); }

function adminId() { return state.user?.id; }

function mapAgendamento(row) {
  return {
    id: row.id,
    clienteNome: row.cliente_nome,
    clienteWa: row.cliente_wa,
    profId: row.prof_id,
    servico: row.servico,
    valor: row.valor,
    duracao: row.duracao,
    data: row.data,
    hora: row.hora,
    status: row.status,
    followUp: row.follow_up,
    criadoEm: row.criado_em,
    finalizadoEm: row.finalizado_em,
  };
}

function mapCliente(row) {
  return { id: row.id, nome: row.nome, email: row.email, wa: row.wa, criadoEm: row.criado_em };
}

function mapServico(row) {
  return { id: row.id, nome: row.nome, preco: row.preco, dur: row.dur };
}

function mapProfissional(row) {
  return { id: row.id, nome: row.nome, funcao: row.funcao, gen: row.gen, sids: row.sids || [], foto: row.foto || null };
}

export const store = {
  subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  get: () => ({ ...state }),

  // ── Auth ────────────────────────────────────────────────────────────────────

  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await store._loadSession(data.session);
    return data;
  },

  register: async ({ empresa, nome, email, wa, password }) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { nome, empresa, wa } },
    });
    if (error) throw error;
    if (data.user) {
      await supabase.from('admins').insert({ id: data.user.id, name: nome, email, empresa, wa });
      await store._loadSession(data.session);
    }
    return data;
  },

  // Redefinir senha do admin via e-mail (Supabase envia o link)
 resetSenhaAdmin: async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://agende-facil-tawny.vercel.app',
  });
  if (error) throw error;
},

  // Atualizar nova senha após clicar no link do e-mail
atualizarSenhaAdmin: async (novaSenha) => {
  console.log('🔍 Hash da URL:', window.location.hash);
  console.log('🔍 Sessão atual:', (await supabase.auth.getSession()).data.session);
  const { error } = await supabase.auth.updateUser({ password: novaSenha });
  if (error) throw error;
},
  logout: async () => {
    await supabase.auth.signOut();
    Object.assign(state, { user: null, tenant: null, clientes: [], servicos: [], profissionais: [], agendamentos: [], diasBloqueados: [], logo: null, acesso: null });
    notify();
  },

  // Atualizar perfil do admin
  atualizarPerfil: async ({ nome, empresa, wa }) => {
    const id = adminId();
    const { error } = await supabase.from('admins').update({ name: nome, empresa, wa }).eq('id', id);
    if (error) throw error;
    await supabase.auth.updateUser({ data: { nome, empresa, wa } });
    state.user = { ...state.user, nome, empresa, wa };
    state.tenant = { ...state.tenant, nome, empresa };
    notify();
  },

  // Excluir conta do admin e todos os dados vinculados
  excluirConta: async () => {
    const id = adminId();
    if (!id) throw new Error('Sem sessão');
    // Deleta todos os dados do admin em cascata (FK ON DELETE CASCADE cuida do resto)
    // Mas deletamos explicitamente config e arquivos do storage
    await supabase.from('config').delete().eq('id_admin', id);
    await supabase.from('agendamentos').delete().eq('id_admin', id);
    await supabase.from('profissionais').delete().eq('id_admin', id);
    await supabase.from('servicos').delete().eq('id_admin', id);
    await supabase.from('clientes').delete().eq('id_admin', id);
    await supabase.from('admins').delete().eq('id', id);
    // Deleta arquivos do storage
    const { data: files } = await supabase.storage.from('fotos').list(id);
    if (files?.length) {
      const paths = files.map(f => `${id}/${f.name}`);
      await supabase.storage.from('fotos').remove(paths);
    }
    // Deleta usuário do Auth
    await supabase.auth.admin?.deleteUser(id);
    await supabase.auth.signOut();
    Object.assign(state, { user: null, tenant: null, clientes: [], servicos: [], profissionais: [], agendamentos: [], diasBloqueados: [], logo: null, acesso: null });
    notify();
  },

  initSession: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) await store._loadSession(session);
    notify();
  },

  _loadSession: async (session) => {
    if (!session) return;
    const meta = session.user.user_metadata;
    state.user = { id: session.user.id, email: session.user.email, nome: meta?.nome || session.user.email.split('@')[0], empresa: meta?.empresa || '', wa: meta?.wa || '' };
    state.tenant = { id: session.user.id, empresa: meta?.empresa || '', nome: meta?.nome || '' };
    await store._loadAll();
    notify();
  },

  _loadAll: async () => {
    const id = adminId();
    if (!id) return;
    state.loading = true; notify();
    const [clientes, servicos, profissionais, agendamentos, config] = await Promise.all([
      supabase.from('clientes').select('*').eq('id_admin', id).order('nome'),
      supabase.from('servicos').select('*').eq('id_admin', id).order('nome'),
      supabase.from('profissionais').select('*').eq('id_admin', id).order('nome'),
      supabase.from('agendamentos').select('*').eq('id_admin', id).order('data').order('hora'),
      supabase.from('config').select('*').eq('id_admin', id).maybeSingle(),
    ]);
    state.clientes      = (clientes.data || []).map(mapCliente);
    state.servicos      = (servicos.data || []).map(mapServico);
    state.profissionais = (profissionais.data || []).map(mapProfissional);
    state.agendamentos  = (agendamentos.data || []).map(mapAgendamento);
    if (config.data) {
      state.horarios       = config.data.horarios || state.horarios;
      state.diasBloqueados = config.data.dias_bloqueados || [];
      state.logo           = config.data.logo || null;
    }
    // Verifica status de acesso/trial
    const { data: acesso } = await supabase.rpc('verificar_acesso', { p_admin_id: id });
    state.acesso = acesso;

    state.loading = false; notify();
  },

  setLogo: async (urlOrNull) => {
    // Se for null, remove a logo
    if (!urlOrNull) {
      state.logo = null;
      notify();
      await store._saveConfig();
      return;
    }
    // Se já for uma URL do Storage (não base64), salva direto
    if (urlOrNull.startsWith('http')) {
      state.logo = urlOrNull;
      notify();
      await store._saveConfig();
      return;
    }
    state.logo = urlOrNull; notify();
    await store._saveConfig();
  },

  uploadLogo: async (file) => {
    const id = adminId();
    const path = `${id}/logo.webp`;
    const { error } = await supabase.storage.from('fotos').upload(path, file, {
      upsert: true,
      contentType: 'image/webp',
    });
    if (error) throw error;
    const { data } = supabase.storage.from('fotos').getPublicUrl(path);
    // Adiciona cache buster para forçar atualização
    const url = data.publicUrl + '?t=' + Date.now();
    state.logo = url;
    notify();
    await store._saveConfig();
    return url;
  },

  // ── Clientes ─────────────────────────────────────────────────────────────────

  addCliente: async (data) => {
    // Se tiver senha, usa RPC que faz o hash corretamente
    if (data.senha) {
      const { data: row, error } = await supabase.rpc('cadastrar_cliente', {
        p_id_admin: adminId(),
        p_nome:     data.nome,
        p_email:    data.email || '',
        p_wa:       data.wa || '',
        p_senha:    data.senha,
      });
      if (error) throw error;
      if (row?.error) throw new Error(row.error);
      // Recarrega lista de clientes para pegar o novo registro completo
      const { data: lista } = await supabase.from('clientes').select('*').eq('id_admin', adminId()).order('nome');
      state.clientes = (lista || []).map(mapCliente);
      notify();
      return;
    }
    // Sem senha — insert direto (cliente sem acesso ao portal)
    const { data: row, error } = await supabase.from('clientes').insert({ id_admin: adminId(), nome: data.nome, email: data.email || null, wa: data.wa || null, criado_em: today() }).select().single();
    if (error) throw error;
    state.clientes.push(mapCliente(row)); notify();
  },

  updateCliente: async (id, data) => {
    const { error } = await supabase.from('clientes').update({ nome: data.nome, email: data.email || null, wa: data.wa || null }).eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.clientes = state.clientes.map(c => c.id === id ? { ...c, ...data } : c); notify();
  },

  deleteCliente: async (id) => {
    const aid = adminId();
    // Apaga códigos de reset do cliente primeiro
    await supabase.from('reset_codigo_cliente').delete()
      .eq('id_admin', aid)
      .eq('email', (state.clientes.find(c=>c.id===id)?.email||''));
    // Apaga agendamentos do cliente
    await supabase.from('agendamentos').delete()
      .eq('id_admin', aid)
      .eq('cliente_id', id);
    // Apaga o cliente
    const { error } = await supabase.from('clientes').delete()
      .eq('id', id)
      .eq('id_admin', aid);
    if (error) throw error;
    state.clientes = state.clientes.filter(c => c.id !== id);
    // Remove agendamentos do estado local também
    state.agendamentos = state.agendamentos.filter(a => a.clienteId !== id);
    notify();
  },

  // ── Serviços ─────────────────────────────────────────────────────────────────

  addServico: async (data) => {
    const { data: row, error } = await supabase.from('servicos').insert({ id_admin: adminId(), nome: data.nome, preco: data.preco, dur: data.dur }).select().single();
    if (error) throw error;
    state.servicos.push(mapServico(row)); notify();
  },

  updateServico: async (id, data) => {
    const { error } = await supabase.from('servicos').update({ nome: data.nome, preco: data.preco, dur: data.dur }).eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.servicos = state.servicos.map(s => s.id === id ? { ...s, ...data } : s); notify();
  },

  deleteServico: async (id) => {
    const { error } = await supabase.from('servicos').delete().eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.servicos = state.servicos.filter(s => s.id !== id); notify();
  },

  // ── Profissionais ─────────────────────────────────────────────────────────────

  addProfissional: async (data) => {
    const { data: row, error } = await supabase.from('profissionais').insert({ id_admin: adminId(), nome: data.nome, funcao: data.funcao || null, gen: data.gen || 'feminino', sids: data.sids || [], foto: data.foto || null }).select().single();
    if (error) throw error;
    state.profissionais.push(mapProfissional(row)); notify();
  },

  updateProfissional: async (id, data) => {
    const { error } = await supabase.from('profissionais').update({ nome: data.nome, funcao: data.funcao || null, gen: data.gen, sids: data.sids || [], foto: data.foto || null }).eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.profissionais = state.profissionais.map(p => p.id === id ? { ...p, ...data } : p); notify();
  },

  uploadFotoProfissional: async (file, profId) => {
    const ext = file.name.split('.').pop();
    const path = `${adminId()}/profissionais/${profId}.${ext}`;
    const { error } = await supabase.storage.from('fotos').upload(path, file, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage.from('fotos').getPublicUrl(path);
    return data.publicUrl;
  },

  deleteProfissional: async (id) => {
    const { error } = await supabase.from('profissionais').delete().eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.profissionais = state.profissionais.filter(p => p.id !== id); notify();
  },

  // ── Agendamentos ──────────────────────────────────────────────────────────────

  addAgendamento: async (data) => {
    const { data: row, error } = await supabase.from('agendamentos').insert({
      id_admin: adminId(), cliente_nome: data.clienteNome, cliente_wa: data.clienteWa || null,
      prof_id: data.profId || null, servico: data.servico, valor: data.valor || 0,
      duracao: data.duracao || 30, data: data.data, hora: data.hora,
      status: 'agendado', follow_up: data.followUp || null, criado_em: today(),
    }).select().single();
    if (error) throw error;
    const ag = mapAgendamento(row);
    state.agendamentos.push(ag); notify();
    return ag;
  },

  updateAgendamento: async (id, data) => {
    const { error } = await supabase.from('agendamentos').update({
      cliente_nome: data.clienteNome, cliente_wa: data.clienteWa || null,
      prof_id: data.profId || null, servico: data.servico, valor: data.valor || 0,
      duracao: data.duracao || 30, data: data.data, hora: data.hora, follow_up: data.followUp || null,
    }).eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.agendamentos = state.agendamentos.map(a => a.id === id ? { ...a, ...data } : a); notify();
  },

  deleteAgendamento: async (id) => {
    const { error } = await supabase.from('agendamentos').delete().eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.agendamentos = state.agendamentos.filter(a => a.id !== id); notify();
  },

  finalizarAgendamento: async (id) => {
    const finalizadoEm = new Date().toISOString();
    const { error } = await supabase.from('agendamentos').update({ status: 'finalizado', finalizado_em: finalizadoEm }).eq('id', id).eq('id_admin', adminId());
    if (error) throw error;
    state.agendamentos = state.agendamentos.map(a => a.id === id ? { ...a, status: 'finalizado', finalizadoEm } : a); notify();
  },

  // ── Horários ──────────────────────────────────────────────────────────────────

  setHorarios: async (data) => { state.horarios = data; notify(); await store._saveConfig(); },
  addDiaBloqueado: async (data) => { if (!state.diasBloqueados.includes(data)) { state.diasBloqueados = [...state.diasBloqueados, data].sort(); notify(); await store._saveConfig(); } },
  removeDiaBloqueado: async (data) => { state.diasBloqueados = state.diasBloqueados.filter(d => d !== data); notify(); await store._saveConfig(); },

  _saveConfig: async () => {
    const id = adminId();
    if (!id) return;
    await supabase.from('config').upsert({ id_admin: id, horarios: state.horarios, dias_bloqueados: state.diasBloqueados, logo: state.logo }, { onConflict: 'id_admin' });
  },
};
